import React from "react";
import {
  MdWarningAmber,
  MdAccessTime,
  MdEventNote,
  MdSpeed,
  MdGroups,
  MdPersonOff,
} from "react-icons/md";
import { CapacityDashboardResponse } from "../../../../interfaces/Capacity";

interface Props {
  data: CapacityDashboardResponse | null;
}

const utilBar = (color: string) =>
  color === "red" ? "bg-red-500" : color === "yellow" ? "bg-amber-500" : "bg-green-500";

const CapacityDashboard: React.FC<Props> = ({ data }) => {
  if (!data) {
    return <div className="py-10 text-center text-sm text-gray-700 dark:text-gray-300">Carregando...</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      {/* KPIs */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Capacity do time (semana)" value={`${data.teamCapacityHours}h`} accent="blue" icon={<MdGroups className="h-5 w-5" aria-hidden="true" />} />
        <Kpi label="Planejado (cronograma)" value={`${data.teamAllocatedHours}h`} accent="cyan" icon={<MdEventNote className="h-5 w-5" aria-hidden="true" />} />
        <Kpi label="Realizado (timesheet)" value={`${data.teamWorkedHours}h`} accent="emerald" icon={<MdAccessTime className="h-5 w-5" aria-hidden="true" />} />
        <Kpi
          label="Utilização"
          value={`${data.utilizationPct}%`}
          tone={data.utilizationPct >= 100 ? "red" : data.utilizationPct >= 80 ? "yellow" : "green"}
          icon={<MdSpeed className="h-5 w-5" aria-hidden="true" />}
        />
      </div>

      {data.overAllocatedCount > 0 && (
        <div className="flex items-center gap-2 rounded-xl bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-300">
          <MdWarningAmber className="h-5 w-5 shrink-0" aria-hidden="true" />
          {data.overAllocatedCount} colaborador(es) sobre-alocado(s) nesta semana.
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        {/* Colaboradores */}
        <div>
          <h4 className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
            Colaboradores
          </h4>
          <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white nina-surface shadow-dv-md dark:border-white/10 dark:bg-navy-800">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-navy-900">
                <tr>
                  <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">Colaborador</th>
                  <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">Cap.</th>
                  <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">Planejado</th>
                  <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">Realizado</th>
                  <th className="w-32 px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">Utilização</th>
                </tr>
              </thead>
              <tbody>
                {data.employees.map((e) => (
                  <tr key={e.employeeId} className="border-b border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700">
                    <td className="px-4 py-3 font-medium text-navy-700 dark:text-white">
                      {e.employeeName}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-300">{e.capacityHours}h</td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-300">{e.allocatedHours}h</td>
                    <td className="px-4 py-3 text-right tabular-nums text-green-600 dark:text-green-400">{e.workedHours}h</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-navy-900">
                          <div
                            className={`h-full ${utilBar(e.utilizationColor)}`}
                            style={{ width: `${Math.min(100, e.utilizationPct)}%` }}
                          />
                        </div>
                        <span className="w-10 text-right text-xs tabular-nums text-gray-700 dark:text-gray-300">
                          {e.utilizationPct}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
                {data.employees.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center">
                      <div className="flex flex-col items-center gap-2 text-gray-700 dark:text-gray-300">
                        <MdPersonOff className="h-6 w-6 text-gray-400" aria-hidden="true" />
                        <span className="text-sm">Nenhum colaborador ativo.</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const ACCENT_TILE: Record<string, string> = {
  blue: "bg-gradient-to-br from-dv-blue-500 to-dv-blue-600 text-white",
  cyan: "bg-gradient-to-br from-dv-cyan-400 to-dv-cyan-600 text-white",
  emerald: "bg-gradient-to-br from-emerald-500 to-emerald-600 text-white",
};
const ACCENT_GLOW: Record<string, string> = {
  blue: "rgba(20,102,227,0.20)",
  cyan: "rgba(28,160,241,0.22)",
  emerald: "rgba(16,185,129,0.20)",
  red: "rgba(239,68,68,0.20)",
  yellow: "rgba(245,158,11,0.20)",
  green: "rgba(16,185,129,0.20)",
};

const Kpi: React.FC<{
  label: string;
  value: React.ReactNode;
  tone?: "green" | "yellow" | "red";
  accent?: "blue" | "cyan" | "emerald";
  icon?: React.ReactNode;
}> = ({ label, value, tone, accent, icon }) => {
  const tile =
    tone === "red"
      ? "bg-gradient-to-br from-red-500 to-red-600 text-white"
      : tone === "yellow"
      ? "bg-gradient-to-br from-amber-500 to-orange-600 text-white"
      : tone === "green"
      ? "bg-gradient-to-br from-emerald-500 to-emerald-600 text-white"
      : accent
      ? ACCENT_TILE[accent]
      : "bg-gradient-to-br from-dv-blue-500 to-dv-blue-600 text-white";
  const glow = ACCENT_GLOW[tone ?? accent ?? "blue"];
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-4 shadow-dv-md transition-all duration-300 hover:-translate-y-1 hover:shadow-dv-lg dark:border-white/10 dark:bg-navy-800 dark:shadow-none">
      <div
        className="pointer-events-none absolute -right-10 -top-12 h-28 w-28 rounded-full blur-2xl transition-opacity duration-300 group-hover:opacity-90"
        style={{ background: glow }}
        aria-hidden="true"
      />
      <div className="relative flex items-center gap-3">
        {icon && (
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${tile}`}
            style={{ boxShadow: `0 12px 30px -10px ${glow}` }}
          >
            {icon}
          </span>
        )}
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-gray-700 dark:text-gray-300">{label}</p>
          <p
            className={`font-display text-2xl font-bold tabular-nums tracking-tight ${
              tone === "red"
                ? "text-red-600 dark:text-red-400"
                : tone === "yellow"
                ? "text-amber-600 dark:text-amber-400"
                : tone === "green"
                ? "text-green-600 dark:text-green-400"
                : "text-navy-700 dark:text-white"
            }`}
          >
            {value}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CapacityDashboard;
